/**
 * A tela de Operação: conferir cada veículo antes de ele ir para a SENATRAN (21/09).
 *
 * **O fluxo, nas palavras da Gisele:** *primeiro você identifica tudo que precisa do
 * veículo — tela operacional, onde ajusta à mão placa, eixos e categoria; depois envia para
 * a SENATRAN as transações já validadas.* Até 20/09 as duas coisas moravam numa tela só, e
 * ela não fazia bem nenhuma das duas. Esta é a primeira metade; `Senatran.tsx` é a segunda.
 *
 * Três colunas, como o mockup (telas 3 e 4): a fila com miniatura, as imagens com os
 * controles de conferência, e à direita o que cada IA disse e o formulário do operador.
 *
 * **KPI e filtro são a mesma régua** (§4.1): cada cartão é um recorte de `consulta.py`
 * dentro do universo `na_operacao`, e o número é o tamanho da fila que o clique abre.
 *
 * **A janela padrão é a das 24 horas**, porque é nela que conferir ainda serve para alguma
 * coisa: passadas as 24 h da passagem o trânsito não pode mais ser enviado. "Todos os
 * capturados" continua a um clique, para quem precisa olhar o que ficou para trás.
 *
 * **O upload é parte da Operação** (decisão de 21/09): "Enviar imagens" abre a tela de
 * lotes, e cada foto analisada volta para esta fila como trânsito de demonstração — com a
 * imagem e as leituras que o módulo de Análise guardou.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import { useSessao } from "../api/contexto-sessao";
import {
  APRESENTACAO_DA_VALIDACAO,
  ehDemonstracao,
  veioDeUpload,
} from "../api/tipos";
import type {
  AcaoDeValidacao,
  AcertoDaIa,
  ItemDaFila,
  PrazoAoVivo,
  Regua,
  ResultadoDaCapacidade,
  SituacaoDasIas,
} from "../api/tipos";
import {
  Badge,
  Botao,
  Estado,
  FiltroContado,
  Painel,
} from "../componentes/base";
import { Icone } from "../componentes/icones";
import { contagem, hora, numeroLongo, reais } from "../formato";
import { useAgora } from "../ganchos/useAgora";
import { useFila } from "../ganchos/useFila";
import { usePerdidosHoje, useProximosPrazos } from "../ganchos/usePrazos";
import { useTransito } from "../ganchos/useTransito";
import { PainelDeValidacao } from "./PainelDeValidacao";
import { PerdaDoDia } from "./PerdaDoDia";
import {
  CARTOES_DA_OPERACAO,
  detalheDoPrazo,
  fotoDoVisorDe,
  miniaturaDe,
} from "./reguas";
import { Visor } from "./Visor";
import type { CaixaDaPlaca } from "./Visor";
import { EVENTO_DO_RELOGIO, agoraDoInquilino } from "../relogio";
import { controlaADemonstracao } from "../componentes/navegacao";

const UNIVERSO = "na_operacao";
const LIMITE = 50;
const INTERVALO_DO_LOTE_MS = 3000;
const ESPERA_MAXIMA_DO_LOTE_MS = 3 * 60_000;

/** A janela das 24 h como filtro de período. Arredondada ao minuto: um `desde` que muda a
 *  cada render recarregaria a fila em laço. */
function inicioDaJanela(): string {
  const agora = agoraDoInquilino();
  agora.setSeconds(0, 0);
  return new Date(agora.getTime() - 24 * 3600_000).toISOString();
}

/** Para onde a decisão levou o trânsito: o cartão da régua que agora o conta. */
const CARTAO_DA_DECISAO: Partial<
  Record<AcaoDeValidacao, { cartao: string; rotulo: string }>
> = {
  SINALIZAR_REVISAO: { cartao: "em_revisao", rotulo: "Ver em revisão" },
  ESCALAR_BACKOFFICE: { cartao: "no_backoffice", rotulo: "Ver no backoffice" },
  DEVOLVER: { cartao: "pendentes_validacao", rotulo: "Ver nos pendentes" },
  SUSPEITAR_FRAUDE: { cartao: "em_revisao", rotulo: "Ver em revisão" },
};

/** Os parâmetros da tela com outro cartão aceso — a janela e o lote continuam. */
function comCartao(parametros: URLSearchParams, cartao: string): string {
  const p = new URLSearchParams(parametros);
  if (cartao === UNIVERSO) p.delete("cartao");
  else p.set("cartao", cartao);
  return p.toString();
}

/** De quanto em quanto tempo a Operação pergunta se a fila mudou (`C-04`). */
const VIGIA_DA_FILA_MS = 10_000;

export function Operacao() {
  const [parametros, definirParametros] = useSearchParams();
  const cartao = parametros.get("cartao") ?? UNIVERSO;
  const todos = parametros.get("janela") === "tudo";
  const [pagina, definirPagina] = useState(0);
  const [busca, definirBusca] = useState("");
  const [selecionado, definirSelecionado] = useState<string | null>(null);
  const [aviso, definirAviso] = useState<Aviso | null>(null);
  const { quem } = useSessao();
  const [janela, definirJanela] = useState(inicioDaJanela);
  // O relógio da demonstração andou (ADR-059): a janela das últimas 24 h é a do tempo novo.
  useEffect(() => {
    const andar = () => definirJanela(inicioDaJanela());
    window.addEventListener(EVENTO_DO_RELOGIO, andar);
    return () => window.removeEventListener(EVENTO_DO_RELOGIO, andar);
  }, []);
  const [doDia, definirDoDia] = useState<Regua | null>(null);
  const [acerto, definirAcerto] = useState<AcertoDaIa | null>(null);
  const [ias, definirIas] = useState<SituacaoDasIas | null>(null);

  // O que veio pela URL (período de Indicadores, recorte nomeado) vale; o cartão e a janela
  // são desta tela e não entram no filtro comum.
  const filtros = useMemo(() => {
    const p = new URLSearchParams();
    for (const [chave, valor] of parametros) {
      if (chave !== "cartao" && chave !== "janela") p.append(chave, valor);
    }
    if (!todos && !p.has("desde")) p.set("desde", janela);
    return p.toString();
  }, [parametros, todos, janela]);

  const fila = useFila({
    universo: UNIVERSO,
    cartoes: CARTOES_DA_OPERACAO.map((c) => c.chave),
    cartao,
    filtros,
    pagina,
    limite: LIMITE,
    // C-04 (CA-08): trânsito novo do pórtico, outro operador decidindo, o motor de prazos
    // tirando um vencido — a fila muda sem ninguém clicar em Atualizar.
    vigiarACada: VIGIA_DA_FILA_MS,
  });
  const transito = useTransito(selecionado);
  // O prazo de 24 h à vista (05/10): quando vence o primeiro desta fila — embaixo do cartão
  // "Vencem 24 h em < 4 h" — e o que o prazo já levou hoje, no topo, ao lado dos aprovados.
  const proximos = useProximosPrazos(filtros, fila.atualizadoEm, UNIVERSO);
  const perdidos = usePerdidosHoje(fila.atualizadoEm);

  // O lote que acabou de ser enviado (Gisele, 25/09: "só carrega quando clico de novo em
  // Operação"). "Conferir na Operação" fica clicável enquanto o lote ainda é analisado, e os
  // registros nascem pelo outbox segundos depois da última foto: a tela abria vazia e não
  // perguntava de novo. Enquanto o filtro do lote não tiver ninguém, ela pergunta a cada
  // 3 s, por até 3 minutos — e diz que está esperando, em vez de "nada para conferir".
  const lote = parametros.get("lote");
  const [esperandoLote, definirEsperandoLote] = useState(false);
  const recarregarDoLote = fila.recarregar;
  const semVeiculosDoLote =
    lote !== null && fila.itens !== null && fila.itens.length === 0;
  useEffect(() => {
    if (!lote || !semVeiculosDoLote) {
      definirEsperandoLote(false);
      return;
    }
    definirEsperandoLote(true);
    const inicio = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - inicio > ESPERA_MAXIMA_DO_LOTE_MS) {
        window.clearInterval(id);
        definirEsperandoLote(false);
        return;
      }
      void recarregarDoLote();
    }, INTERVALO_DO_LOTE_MS);
    return () => window.clearInterval(id);
  }, [lote, semVeiculosDoLote, recarregarDoLote]);

  // Voltar à primeira página quando o recorte muda: a página 7 de "Pendentes" não existe
  // em "Isenção sugerida".
  useEffect(() => definirPagina(0), [cartao, filtros]);

  const carregarDoDia = useCallback(async () => {
    try {
      definirDoDia(await api<Regua>("/tollvision/regua?cartao=aprovados_hoje"));
    } catch {
      definirDoDia(null);
    }
  }, []);
  useEffect(() => void carregarDoDia(), [carregarDoDia]);

  // Quanto o OCR acertou (conferência humana, 7 dias) e quais IAs estão de pé: as duas
  // perguntas dos testes de 21/09 que antes só se respondiam no terminal.
  const carregarIas = useCallback(async () => {
    const [a, i] = await Promise.allSettled([
      api<AcertoDaIa>("/tollvision/acerto?dias=7"),
      api<SituacaoDasIas>("/visao/situacao"),
    ]);
    definirAcerto(a.status === "fulfilled" ? a.value : null);
    definirIas(i.status === "fulfilled" ? i.value : null);
  }, []);
  useEffect(() => void carregarIas(), [carregarIas]);

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (fila.itens ?? []).filter(
      (t) =>
        !termo ||
        (t.placa ?? "").toLowerCase().includes(termo) ||
        t.praca.toLowerCase().includes(termo),
    );
  }, [fila.itens, busca]);

  useEffect(() => {
    if (lista.length === 0) definirSelecionado(null);
    else if (!selecionado || !lista.some((t) => t.id === selecionado))
      definirSelecionado(lista[0]!.id);
  }, [lista, selecionado]);

  /**
   * O prazo de 24 h andando na fila (O-03, versão enxuta). O relógio só bate por segundo
   * quando há trânsito a menos de uma hora do vencimento; e, a menos de dois minutos, a
   * fila se recarrega a cada 20 s — é assim que o trânsito vencido **sai** da tela quando o
   * motor de prazos o marca como não enviável, e o aviso de perda aparece.
   */
  const vencimentos = (fila.itens ?? [])
    .filter((t) => t.proximo_prazo_tipo === "ENVIO_24H" && t.proximo_prazo_em)
    .map((t) => new Date(t.proximo_prazo_em!).getTime());
  const maisPerto = vencimentos.length ? Math.min(...vencimentos) : Infinity;
  const agora = useAgora(
    1000,
    maisPerto - agoraDoInquilino().getTime() < 3_700_000,
  );
  const vigiar = maisPerto - agora.getTime() < 120_000;
  const recarregarFila = fila.recarregar;
  useEffect(() => {
    if (!vigiar) return;
    const id = window.setInterval(() => void recarregarFila(), 20_000);
    return () => window.clearInterval(id);
  }, [vigiar, recarregarFila]);

  // Quem venceu à vista e depois sumiu da fila perdeu o prazo — a não ser que o operador o
  // tenha aprovado nesse meio-tempo, e aí quem o tirou da fila foi a aprovação.
  const vencidosAVista = useRef(new Map<string, ItemDaFila>());
  const aprovadosAgora = useRef(new Set<string>());
  const [decididos, definirDecididos] = useState<Decidido[]>([]);
  // Trocar de cartão não é perder prazo: o que sumiu da lista foi para outro cartão. A
  // vigília recomeça com a lista nova (29/09 — trocar de "Pendentes" para "Em revisão"
  // avisava que os vencidos de "Pendentes" tinham virado não enviáveis).
  useEffect(() => vencidosAVista.current.clear(), [cartao, filtros]);
  useEffect(() => {
    if (fila.itens === null) return;
    const itens = fila.itens;
    const presentes = new Set(itens.map((t) => t.id));
    let perdeuAgora = false;
    for (const [id, t] of vencidosAVista.current) {
      if (presentes.has(id)) continue;
      vencidosAVista.current.delete(id);
      if (aprovadosAgora.current.has(id)) continue;
      definirAviso({
        cor: "vermelho",
        texto: `${t.placa ?? "Trânsito"} perdeu o prazo de 24 h e virou não enviável: ${
          t.valor_centavos != null ? reais(t.valor_centavos) : "a tarifa"
        } sem cobrança e sem autuação. Entra em "Perderam o prazo hoje", aqui em cima, e nos Indicadores.`,
      });
      perdeuAgora = true;
    }
    // O cartão "vencem em < 4 h" ficaria aberto numa fila vazia: volta para a fila toda.
    if (perdeuAgora && cartao === "prazo_24h_critico") {
      const p = new URLSearchParams(parametros);
      p.delete("cartao");
      definirParametros(p);
    }
    for (const t of itens) {
      if (
        t.proximo_prazo_tipo === "ENVIO_24H" &&
        t.proximo_prazo_em &&
        new Date(t.proximo_prazo_em).getTime() <= agora.getTime()
      )
        vencidosAVista.current.set(t.id, t);
    }
  }, [fila.itens, agora, cartao, parametros, definirParametros]);

  const [simulando, definirSimulando] = useState(false);
  const podeSimular = controlaADemonstracao(quem);
  async function simularPrazo() {
    definirSimulando(true);
    try {
      const r = await api<PrazoAoVivo>("/demonstracao/prazo-ao-vivo", {
        method: "POST",
        corpo: { minutos: 3 },
      });
      const p = new URLSearchParams(parametros);
      p.set("cartao", "prazo_24h_critico");
      definirParametros(p);
      definirSelecionado(r.transito_id);
      definirAviso({
        cor: "amarelo",
        texto: `${r.placa} passou pelo pórtico há quase 24 h e vence em 3 minutos. Aprovado e enviado a tempo, é ${reais(r.valor_centavos)} arrecadado; se ninguém agir, é perda.`,
      });
      await fila.recarregar();
    } catch (falha) {
      definirAviso({
        cor: "vermelho",
        texto:
          falha instanceof ErroDaApi
            ? falha.message
            : "Não foi possível simular o prazo.",
      });
    } finally {
      definirSimulando(false);
    }
  }

  const indice = lista.findIndex((t) => t.id === selecionado);
  const navegar = useCallback(
    (passo: -1 | 1) => {
      const proximo = lista[indice + passo];
      if (proximo) definirSelecionado(proximo.id);
    },
    [lista, indice],
  );

  /**
   * O ritmo da conferência: ↓/J o próximo, ↑/K o anterior — fora de campo de texto. Os
   * atalhos de decisão (A, R, B, N) moram no painel da conferência; os de imagem (P, F,
   * 1–3), no visor. Cada um atende o que é seu.
   */
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const alvo = e.target;
      if (
        alvo instanceof HTMLElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(alvo.tagName)
      )
        return;
      const tecla = e.key.toLowerCase();
      if (tecla === "arrowdown" || tecla === "j") {
        e.preventDefault();
        navegar(1);
      } else if (tecla === "arrowup" || tecla === "k") {
        e.preventDefault();
        navegar(-1);
      }
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [navegar]);

  // O próximo já carregado: a imagem dele baixa enquanto o operador confere este, e o clique
  // em "Aprovar" não termina numa tela preta esperando a foto seguinte. A foto **do visor**,
  // inteira — a miniatura a lista já baixou —, e ela fica no navegador (tarefa 20).
  useEffect(() => {
    const proximo = lista[indice + 1];
    const src = proximo ? fotoDoVisorDe(proximo) : null;
    if (src) new Image().src = src;
  }, [lista, indice]);

  function acender(chave: string) {
    const p = new URLSearchParams(parametros);
    if (chave === UNIVERSO) p.delete("cartao");
    else p.set("cartao", chave);
    definirParametros(p);
  }

  function trocarJanela(tudo: boolean) {
    const p = new URLSearchParams(parametros);
    if (tudo) p.set("janela", "tudo");
    else p.delete("janela");
    definirJanela(inicioDaJanela());
    definirParametros(p);
  }

  async function depoisDaDecisao(
    saiu: boolean,
    texto?: string,
    decisao?: AcaoDeValidacao,
  ) {
    if (saiu && selecionado) aprovadosAgora.current.add(selecionado);
    // O aviso leva aonde o trânsito foi (Gisele, 29/09: "quando mando para revisão, quero
    // clicar e ver os que enviei").
    const destino = decisao ? CARTAO_DA_DECISAO[decisao] : undefined;
    if (texto)
      definirAviso({
        texto,
        cor: "verde",
        ...(decisao === "APROVAR"
          ? {
              link: {
                para: "/senatran?cartao=aguardando_envio",
                rotulo: "Ver na SENATRAN",
              },
            }
          : destino
            ? {
                link: {
                  para: `/operacao?${comCartao(parametros, destino.cartao)}`,
                  rotulo: destino.rotulo,
                },
              }
            : {}),
      });
    // O decidido sai do recorte da Operação — mas a Gisele (21/09) esperava vê-lo mudar de
    // situação, e não sumir. Ele fica numa faixa "decididos agora", com o selo novo.
    if (saiu && selecionado && decisao) {
      const item = lista.find((t) => t.id === selecionado);
      definirDecididos((d) =>
        [
          { id: selecionado, placa: item?.placa ?? null, acao: decisao },
          ...d.filter((x) => x.id !== selecionado),
        ].slice(0, 5),
      );
    }
    if (saiu && selecionado) {
      // O decidido sai do recorte: a seleção anda para o próximo antes de a fila chegar,
      // e é esse o ritmo da conferência — aprovar e já estar olhando o seguinte.
      const proximo = lista[indice + 1] ?? lista[indice - 1];
      definirSelecionado(proximo?.id ?? null);
    }
    await Promise.all([
      fila.recarregar(),
      transito.recarregar(),
      carregarDoDia(),
      saiu ? carregarIas() : Promise.resolve(),
    ]);
  }

  // O decidido que está na lista aberta (foi para "Em revisão" e o cartão aceso é esse)
  // aparece na lista, e não duas vezes.
  const foraDaLista = decididos.filter(
    (d) => !lista.some((t) => t.id === d.id),
  );

  const regua = fila.regua;
  const indiceDeDivergencia =
    regua && regua[UNIVERSO]
      ? (100 * (regua.divergentes ?? 0)) / regua[UNIVERSO]!
      : null;
  const doPeriodo = parametros.has("desde") || parametros.has("recorte");

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Operação
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            Conferir placa, eixos e categoria antes do envio à SENATRAN
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Estatistica
            rotulo="Aprovados hoje"
            valor={numeroLongo(doDia?.aprovados_hoje)}
          />
          <PerdaDoDia perdidos={perdidos} />
          <Estatistica
            rotulo="Acerto do OCR · 7 dias"
            titulo={
              acerto && acerto.conferidos > 0
                ? `${acerto.placa_certa} certas, ${acerto.placa_corrigida} corrigidas, ${acerto.placa_nao_lida} não lidas em ${acerto.conferidos} conferências`
                : "Medido nas aprovações do operador: ainda não há conferências."
            }
            valor={
              acerto?.taxa_placa == null
                ? "—"
                : `${acerto.taxa_placa.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
            }
            cor={
              acerto?.taxa_placa == null
                ? undefined
                : acerto.taxa_placa >= 90
                  ? "verde"
                  : acerto.taxa_placa >= 70
                    ? "amarelo"
                    : "vermelho"
            }
          />
          <Estatistica
            rotulo="Divergência"
            valor={
              indiceDeDivergencia == null
                ? "—"
                : `${indiceDeDivergencia.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
            }
          />
          <PilulaDasIas situacao={ias} />
          {podeSimular && (
            <Botao
              papel="atencao"
              disabled={simulando}
              title="Demonstração: grava um trânsito cuja passagem foi há quase 24 h. O prazo vence de verdade, pelo motor de prazos."
              onClick={() => void simularPrazo()}
            >
              <Icone.Relogio />
              {simulando ? "Gravando…" : "Simular prazo vencendo"}
            </Botao>
          )}
          <Link
            to="/operacao/imagens"
            className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-azul-cheio bg-azul-cheio px-2.5 text-[11.5px] font-semibold text-white hover:bg-azul"
          >
            <Icone.Subir />
            Enviar imagens
          </Link>
        </div>
      </div>

      {/* Um bloco só para filtro, aviso e régua: a moldura divide a tela em três linhas
          (título, isto, trabalho), e cada um deles solto virava uma linha a mais — com
          aviso na tela, a régua caía na linha elástica e os cartões esticavam (21/09). */}
      <div className="grid gap-2">
        {parametros.has("lote") && (
          <div className="flex flex-wrap items-center gap-2 rounded-cartao border border-verde-borda bg-verde-fundo px-3 py-2">
            <Badge cor="verde">lote enviado</Badge>
            <span className="text-[11.5px] text-tinta-2">
              {esperandoLote
                ? "Criando os registros do lote… a lista aparece sozinha em alguns segundos."
                : "Só os veículos do lote que você acabou de enviar — um registro por veículo, com as fotos dele no carrossel do meio (← →)."}
            </span>
            <Botao
              onClick={() => {
                const p = new URLSearchParams(parametros);
                p.delete("lote");
                p.delete("janela");
                definirParametros(p);
              }}
            >
              Ver a fila toda
            </Botao>
          </div>
        )}

        {doPeriodo && (
          <div className="flex flex-wrap items-center gap-2 rounded-cartao border border-azul-borda bg-azul-fundo px-3 py-2">
            <Badge cor="azul">filtro de Indicadores</Badge>
            <span className="text-[11.5px] text-tinta-2">
              A fila e a régua abaixo estão dentro do período e do recorte
              escolhidos lá.
            </span>
            <Botao onClick={() => definirParametros({})}>
              <Icone.Barrado />
              Remover o filtro
            </Botao>
          </div>
        )}

        {aviso && (
          <div
            role="status"
            className={`flex flex-wrap items-center gap-2 rounded-cartao border px-3 py-2 ${COR_DO_AVISO[aviso.cor]}`}
          >
            <span>
              {aviso.cor === "verde" ? <Icone.Certo /> : <Icone.Alerta />}
            </span>
            <span className="text-[11.5px] text-tinta">{aviso.texto}</span>
            {aviso.link && (
              <Link
                to={aviso.link.para}
                className="rounded-botao border border-verde-borda px-2 py-1 text-[11px] font-semibold text-verde hover:bg-verde-fundo"
              >
                {aviso.link.rotulo} →
              </Link>
            )}
            <Botao onClick={() => definirAviso(null)}>Fechar</Botao>
          </div>
        )}

        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label="Régua da operação"
        >
          {CARTOES_DA_OPERACAO.map((c) => (
            <FiltroContado
              key={c.chave}
              rotulo={c.rotulo}
              valor={regua ? (regua[c.chave] ?? 0) : null}
              icone={c.icone}
              {...(c.severidade ? { severidade: c.severidade } : {})}
              ativo={cartao === c.chave}
              aoClicar={() => acender(c.chave)}
              detalhe={detalheDoPrazo(
                c.chave,
                regua?.[c.chave] ?? null,
                proximos,
              )}
            />
          ))}
          <span className="ml-auto hidden self-center text-[10.5px] text-tinta-3 2xl:inline">
            Atalhos: <b className="mono">↑↓</b> navegar ·{" "}
            <b className="mono">A</b> aprovar · <b className="mono">R</b>{" "}
            revisão · <b className="mono">B</b> backoffice ·{" "}
            <b className="mono">P</b> placa · <b className="mono">F</b> tela
            cheia
          </span>
        </div>
      </div>

      <div className="grid min-h-0 grid-cols-1 gap-3 xl:grid-cols-[300px_minmax(0,1fr)_380px]">
        <Painel
          janelaNoCelular
          titulo="Fila de validação"
          acoes={
            !doPeriodo && (
              <span className="flex gap-1" role="group" aria-label="Janela">
                <Chip ativo={!todos} aoClicar={() => trocarJanela(false)}>
                  24 h
                </Chip>
                <Chip ativo={todos} aoClicar={() => trocarJanela(true)}>
                  Todos
                </Chip>
              </span>
            )
          }
          rodape={
            <Paginacao
              pagina={pagina}
              limite={LIMITE}
              total={fila.total}
              naPagina={fila.itens?.length ?? 0}
              aoMudar={definirPagina}
              aoAtualizar={() => void fila.recarregar()}
            />
          }
        >
          <label className="flex items-center gap-2 rounded-botao border border-linha bg-painel-2 px-2.5 py-1.5">
            <span className="text-tinta-3">
              <Icone.Busca />
            </span>
            <input
              value={busca}
              onChange={(e) => definirBusca(e.target.value)}
              placeholder="Buscar nesta página por placa ou praça"
              aria-label="Buscar"
              className="w-full bg-transparent text-tinta placeholder:text-tinta-3"
            />
          </label>

          {foraDaLista.length > 0 && (
            <ul aria-label="Decididos agora" className="grid gap-1.5">
              {foraDaLista.map((d) => (
                <li key={`decidido-${d.id}`}>
                  <Link
                    to={
                      d.acao === "APROVAR"
                        ? "/senatran?cartao=aguardando_envio"
                        : `/operacao?${comCartao(parametros, CARTAO_DA_DECISAO[d.acao]?.cartao ?? UNIVERSO)}`
                    }
                    className="flex items-center gap-2 rounded-botao border border-linha bg-painel-2 px-2.5 py-1.5 opacity-80 hover:opacity-100"
                    title="Decidido agora — saiu da fila de conferência"
                  >
                    <span className="mono font-bold text-tinta">
                      {d.placa ?? "sem placa"}
                    </span>
                    <span className="ml-auto">
                      <Badge cor={SELO_DA_DECISAO[d.acao].cor}>
                        {SELO_DA_DECISAO[d.acao].rotulo}
                      </Badge>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {fila.erro ? (
            <Estado
              tipo={fila.erro.semLicenca ? "sem-licenca" : "erro"}
              titulo={
                fila.erro.semLicenca
                  ? "Esta organização não licenciou o módulo TollVision."
                  : "Não foi possível carregar a fila."
              }
              detalhe={fila.erro.message}
            />
          ) : fila.itens === null ? (
            <Estado tipo="carregando" titulo="Carregando a fila…" />
          ) : lista.length === 0 ? (
            <Estado
              tipo="vazio"
              titulo={
                todos || doPeriodo
                  ? "Nada para conferir neste filtro."
                  : "Nada para conferir nas últimas 24 horas."
              }
              detalhe={
                todos || doPeriodo
                  ? "Troque o cartão no topo ou limpe a busca."
                  : "Envie imagens para a fila, ou veja todos os capturados."
              }
            />
          ) : (
            <ul aria-label="Fila de validação" className="grid gap-1.5">
              {lista.map((t) => (
                <li key={t.id}>
                  <ItemDaLista
                    transito={t}
                    selecionado={t.id === selecionado}
                    agora={agora}
                    aoSelecionar={() => definirSelecionado(t.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </Painel>

        <Visor
          detalhe={transito.detalhe}
          caixaDaPlaca={caixaDaPlaca(transito.analise)}
          analiseDasVistas={transito.analiseDasVistas}
        />

        <PainelDeValidacao
          detalhe={transito.detalhe}
          analise={transito.analise}
          analiseDasVistas={transito.analiseDasVistas}
          carregando={transito.carregando}
          erro={transito.erro}
          posicao={indice + 1}
          total={lista.length}
          aoNavegar={navegar}
          aoMudar={depoisDaDecisao}
        />
      </div>
    </>
  );
}

/** A caixa que o OCR da análise devolveu, se devolveu. */
function caixaDaPlaca(analise: ResultadoDaCapacidade[]): CaixaDaPlaca | null {
  const ocr = analise.find((r) => r.capacidade === "ocr_placa");
  const caixa = (ocr?.valor as { caixa?: CaixaDaPlaca } | null)?.caixa;
  return caixa && caixa.largura > 0 && caixa.altura > 0 ? caixa : null;
}

interface Aviso {
  texto: string;
  cor: "verde" | "amarelo" | "vermelho";
  /** Para onde o aviso leva: a aprovação diz "está na SENATRAN" e mostra o caminho. */
  link?: { para: string; rotulo: string };
}

/** Um trânsito decidido nesta sessão: sai do recorte da Operação, mas não some da vista. */
interface Decidido {
  id: string;
  placa: string | null;
  acao: AcaoDeValidacao;
}

const SELO_DA_DECISAO: Record<
  AcaoDeValidacao,
  { rotulo: string; cor: "verde" | "amarelo" | "vermelho" | "cinza" }
> = {
  APROVAR: { rotulo: "Aprovado → SENATRAN", cor: "verde" },
  SINALIZAR_REVISAO: { rotulo: "Em revisão", cor: "amarelo" },
  ESCALAR_BACKOFFICE: { rotulo: "No backoffice", cor: "vermelho" },
  DEVOLVER: { rotulo: "Devolvido", cor: "cinza" },
  SUSPEITAR_FRAUDE: { rotulo: "Suspeita de fraude", cor: "amarelo" },
  CONFIRMAR_FRAUDE: { rotulo: "Fraude confirmada", cor: "vermelho" },
};

const COR_DO_AVISO = {
  verde: "border-verde-borda bg-verde-fundo text-verde",
  amarelo: "border-amarelo-borda bg-amarelo-fundo text-amarelo",
  vermelho: "border-vermelho-borda bg-vermelho-fundo text-vermelho",
} as const;

const COR_DO_VALOR = {
  verde: "text-verde",
  amarelo: "text-amarelo",
  vermelho: "text-vermelho",
} as const;

function Estatistica({
  rotulo,
  valor,
  cor,
  titulo,
}: {
  rotulo: string;
  valor: string;
  cor?: keyof typeof COR_DO_VALOR | undefined;
  titulo?: string;
}) {
  return (
    <span
      title={titulo}
      className="inline-flex items-baseline gap-1.5 rounded-pilula border border-linha bg-painel-2 px-2.5 py-1 text-[11px] text-tinta-3"
    >
      {rotulo}
      <b
        className={`mono text-[12.5px] ${cor ? COR_DO_VALOR[cor] : "text-tinta"}`}
      >
        {valor}
      </b>
    </span>
  );
}

/** "Quais IAs estão ativas?" num relance, e o clique abre o detalhe na Administração. */
function PilulaDasIas({ situacao }: { situacao: SituacaoDasIas | null }) {
  if (!situacao?.ias) return null;
  const ativas = situacao.ias.filter((i) => i.ativa);
  const cor = !situacao.servico_no_ar
    ? "text-vermelho border-vermelho-borda bg-vermelho-fundo"
    : ativas.length === situacao.ias.length
      ? "text-verde border-verde-borda bg-verde-fundo"
      : "text-amarelo border-amarelo-borda bg-amarelo-fundo";
  return (
    <Link
      to="/administracao?secao=ias"
      title={
        situacao.servico_no_ar
          ? situacao.ias
              .map((i) => `${i.rotulo}: ${i.ativa ? "ativa" : "não servida"}`)
              .join("\n")
          : (situacao.motivo ?? "Serviço de visão fora do ar")
      }
      className={`inline-flex items-center gap-1.5 rounded-pilula border px-2.5 py-1 text-[11px] font-semibold ${cor}`}
    >
      <Icone.Alvo />
      {situacao.servico_no_ar
        ? `IAs ativas ${ativas.length}/${situacao.ias.length}`
        : "IAs fora do ar"}
    </Link>
  );
}

export function Chip({
  ativo,
  aoClicar,
  children,
  desabilitado = false,
  titulo,
}: {
  ativo: boolean;
  aoClicar: () => void;
  children: ReactNode;
  desabilitado?: boolean;
  titulo?: string | undefined;
}) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      disabled={desabilitado}
      title={titulo}
      onClick={aoClicar}
      className={`rounded-pilula border px-2 py-0.5 text-[10.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-45 ${
        ativo
          ? "border-azul bg-realce text-tinta"
          : "border-linha bg-painel-2 text-tinta-3 hover:text-tinta-2"
      }`}
    >
      {children}
    </button>
  );
}

export function Paginacao({
  pagina,
  limite,
  total,
  naPagina,
  aoMudar,
  aoAtualizar,
}: {
  pagina: number;
  limite: number;
  total: number | null;
  naPagina: number;
  aoMudar: (pagina: number) => void;
  aoAtualizar: () => void;
}) {
  const inicio = naPagina === 0 ? 0 : pagina * limite + 1;
  const fim = pagina * limite + naPagina;
  const ultima =
    total == null ? pagina : Math.max(0, Math.ceil(total / limite) - 1);
  return (
    <>
      <span className="mono">
        {inicio}–{fim} de {numeroLongo(total)}
      </span>
      <span className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Página anterior"
          disabled={pagina === 0}
          onClick={() => aoMudar(pagina - 1)}
          className="rounded-botao border border-linha bg-painel-2 px-2 py-0.5 text-tinta-2 disabled:opacity-40"
        >
          ‹
        </button>
        <button
          type="button"
          aria-label="Próxima página"
          // A lista do servidor tem teto de 10 mil de deslocamento: além disso, o caminho é
          // filtrar, não folhear.
          disabled={pagina >= ultima || (pagina + 1) * limite > 10_000}
          onClick={() => aoMudar(pagina + 1)}
          className="rounded-botao border border-linha bg-painel-2 px-2 py-0.5 text-tinta-2 disabled:opacity-40"
        >
          ›
        </button>
        <Botao onClick={aoAtualizar}>
          <Icone.Recarga />
          Atualizar
        </Botao>
      </span>
    </>
  );
}

/**
 * Um item da fila, com a cor do que ele pede (feedback de 21/09: "melhorar a
 * identificação"): **vermelho** quando a IA não leu placa nenhuma, **amarelo** quando falta
 * conferir, verde quando já foi aprovado. Isenção e divergência viram selo com texto, não
 * ícone solto.
 */
function ItemDaLista({
  transito,
  selecionado,
  agora,
  aoSelecionar,
}: {
  transito: ItemDaFila;
  selecionado: boolean;
  agora: Date;
  aoSelecionar: () => void;
}) {
  const prazo =
    transito.proximo_prazo_tipo === "ENVIO_24H" && transito.proximo_prazo_em
      ? contagem(transito.proximo_prazo_em, agora)
      : null;
  const src = miniaturaDe(transito);
  const [semImagem, definirSemImagem] = useState(false);
  const situacao = APRESENTACAO_DA_VALIDACAO[transito.validacao];
  const semPlaca = !transito.placa;
  const semPlacaPendente = semPlaca && transito.validacao === "PENDENTE";
  const faixa = semPlaca
    ? "border-l-vermelho"
    : situacao.cor === "verde"
      ? "border-l-verde"
      : "border-l-amarelo";
  const confianca = transito.confianca;
  return (
    <button
      type="button"
      aria-current={selecionado}
      onClick={aoSelecionar}
      className={`grid w-full grid-cols-[64px_1fr_auto] items-center gap-2 rounded-[7px] border border-l-[3px] px-2 py-1.5 text-left transition-colors ${faixa} ${
        selecionado
          ? "border-azul bg-realce"
          : "border-transparent bg-painel-2 hover:bg-realce/60"
      }`}
    >
      <span
        data-tema="escuro"
        className="grid aspect-[4/3] place-items-center overflow-hidden rounded-[5px] bg-mesa"
      >
        {src && !semImagem ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            onError={() => definirSemImagem(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-[9px] text-tinta-3">sem imagem</span>
        )}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          {semPlaca ? (
            <b className="text-[11.5px] font-bold text-vermelho uppercase">
              placa não lida
            </b>
          ) : (
            <b className="mono text-[13px] text-tinta">{transito.placa}</b>
          )}
        </span>
        <small className="block truncate text-[11px] text-tinta-3">
          {hora(transito.timestamp_passagem)} · {transito.praca}
        </small>
        <span className="mt-0.5 flex flex-wrap items-center gap-1 text-[10.5px]">
          <span className="text-tinta-3">
            {veioDeUpload(transito)
              ? "upload"
              : ehDemonstracao(transito)
                ? "simulado"
                : "pórtico"}
          </span>
          {confianca != null && (
            <span
              className={`mono ${confianca >= 90 ? "text-verde" : confianca >= 70 ? "text-amarelo" : "text-vermelho"}`}
            >
              OCR{" "}
              {confianca.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}%
            </span>
          )}
          {transito.isento || transito.na_lista_de_isentos ? (
            <Badge cor="verde">isento</Badge>
          ) : null}
          {transito.fotos > 1 && (
            <span className="text-tinta-3">{transito.fotos} fotos</span>
          )}
          {transito.divergencia && <Badge cor="vermelho">diverge</Badge>}
          {prazo && prazo.urgencia !== "folga" && (
            <span
              role="timer"
              className={`mono rounded-badge border px-1 text-[10px] font-bold ${
                prazo.urgencia === "atencao"
                  ? "border-amarelo-borda bg-amarelo-fundo text-amarelo"
                  : "border-vermelho-borda bg-vermelho-fundo text-vermelho"
              }`}
            >
              {prazo.urgencia === "vencido"
                ? "24 h vencidas"
                : `vence ${prazo.texto}`}
            </span>
          )}
        </span>
      </span>
      {/* A situação ganha de "sem placa" quando alguém já a decidiu: na lista de "Em
          revisão", o selo diz "Em revisão" (29/09). */}
      <Badge cor={semPlacaPendente ? "vermelho" : situacao.cor}>
        {semPlacaPendente ? "sem placa" : situacao.rotulo}
      </Badge>
    </button>
  );
}
